<template>
  <div>
    <!-- 2-column layout -->
    <div class="employment-info-container">
      <div class="profile-info-block">
        <!-- Section: 2 columns (header + 登録日 in col-8, 最終更新日 in col-4) -->
        <div class="row mb-3">
          <div class="col-7">
            <div class="section-header-inline row">
              <span class="section-header col-5">{{ sectionHeaderLabel }}</span>
              <div class="section-content col-6">
                <span class="section-label">登録日：</span>
                <span class="section-value">{{ joinedDate }}</span>
              </div>
            </div>
          </div>
          <div class="col-4">
            <div class="section-row">
              <span class="section-label">最終更新日：</span>
              <span class="section-value">{{ updateDate }}</span>
            </div>
          </div>
        </div>

        <!-- middle section: 2 columns -->
        <div class="row">
          <!-- Left Main Content -->
          <div class="col-7">
            <!-- Recent Company -->
            <div class="mb-3" v-if="history.lastCompany">
              <div class="section-row">
                <span class="section-label">直近在籍企業：</span>
                <span
                  class="section-value company-name"
                  v-html="highlightText(['recent_registered_company_name'], history.lastCompany)"
                ></span>
              </div>
              <div class="section-row">
                <span class="section-label">在籍期間：</span>
                <span class="section-value" v-if="!enrollment_period">
                  未設定
                </span>
                <span class="section-value" v-else-if="(history.currently_enrolled === false || (history.currently_enrolled == null && enrollment_period && enrollment_period.to)) && enrollment_period && enrollment_period.from && enrollment_period.from.year">
                  {{ `${enrollment_period.from.year}年${enrollment_period.from.month}月～ ${enrollment_period.to.year}年${enrollment_period.to.month}月` }}
                  （離職中）
                </span>
                <span class="section-value" v-else-if="(history.currently_enrolled === true || (history.currently_enrolled == null && enrollment_period && !enrollment_period.to)) && enrollment_period && enrollment_period.from && enrollment_period.from.year">
                  {{ `${enrollment_period.from.year}年${enrollment_period.from.month}月～ ` }}
                  （在職中）
                </span>
              </div>
              <div class="section-row">
                <span class="section-label">雇用形態：</span>
                <span class="section-value">{{ history.status.title }}</span>
              </div>
            </div>

            <!-- Recent Job -->
            <div class="mb-3">
              <div class="section-row">
                <span class="section-label">直近職種：</span>
                <span class="section-value">{{ lastJob }}</span>
              </div>
            </div>

            <!-- Position -->
            <div class="mb-3" v-if="history.position">
              <div class="section-row">
                <span class="section-label">役職：</span>
                <span class="section-value">{{ history.position.title }}</span>
              </div>
            </div>
          </div>

          <!-- Right Meta Content -->
          <div class="col-4">
            <div class="section-row">
              <span class="section-label">経験社数：</span>
              <span class="section-value">{{ history.experienceCompanies }}社</span>
            </div>
            <div class="section-row" v-if="history.englishSkill">
              <span class="section-label">英語力：</span>
              <span class="section-value">{{ history.englishSkill.title }}</span>
            </div>
            <div class="section-row">
              <span class="section-label">直近年収：</span>
              <span class="section-value">{{ history.lastYearIncome }}万円</span>
            </div>
            <div class="mt-3" v-if="events && events.length > 0">
              <div class="section-label">申込みイベント：</div>
              <div v-for="(event, index) in events" :key="index" class="event-item">
                <span class="section-value">{{ formatDate(event.event_date.from) }}_{{ event.event_name }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Full Width Bottom Sections -->
        <div class="mt-1">
          <!-- Resume -->
          <div class="mb-3">
            <div class="section-row">
              <span class="section-label">職務経歴書：</span>
              <a
                v-if="history.resume"
                @click="onLinkClick('resume')"
                :class="[resumeLoading ? 'disabled' : ' ', 'preview-resume']"
                >{{ history.resume }}</a
              >
            </div>
            <div class="section-row">
              <span class="section-label ms-4.2">履歴書：</span>
              <a
                v-if="history.cv"
                @click="onLinkClick('cv')"
                :class="[resumeLoading ? 'disabled' : ' ', 'preview-resume']"
                >{{ history.cv }}</a
              >
            </div>
          </div>

          <!-- Job Summary -->
          <div class="mb-3">
            <div class="section-label">職務要約：</div>
            <div class="section-text" v-html="highlightText(['job_summary'], history.summary)"></div>
          </div>

          <!-- Considerations -->
          <div class="mb-3">
            <div class="section-label">配慮事項について：</div>
            <div class="section-text">{{ history.career_change_reason }}</div>
          </div>

          <!-- Resume Information -->
          <div class="mb-3">
            <div class="section-label">職務経歴書情報</div>
            <div
              class="section-text resume-text"
              v-html="highlightText(['resumeContent'], history.resumeContent)"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
export default {
  name: 'EmploymentInfo',
  props: {
    history: {
      required: true,
    },
    resumeLoading: {
      required: true,
    },
    eventHistory: {
      required: true,
    },
    applicant: {
      required: true,
    },
    highlights: {
      type: Array,
      default: () => [],
    },
  },
  methods: {
    onLinkClick(type) {
      this.$emit('previewClick', type);
    },
    formatDate(date) {
      return moment(date).utc().format('YYYY-MM-DD');
    },
    highlightText(paths, originalText) {
      if (!this.highlights || !this.highlights.length) {
        return originalText;
      }

      const highlights = this.highlights.filter((h) => paths.includes(h.path));
      if (highlights.length === 0) {
        return originalText;
      }

      let highlightedText = originalText;

      for (let highlight of highlights) {
        // Sort hit texts by length (longest first) to handle overlapping matches
        const hitTexts = highlight.texts
          .filter((t) => t.type === 'hit')
          .sort((a, b) => b.value.length - a.value.length);

        // Process each hit text separately
        for (let hitText of hitTexts) {
          const searchRegex = new RegExp(
            this.escapeRegExp(hitText.value),
            'gi'
          );
          highlightedText = highlightedText.replace(
            searchRegex,
            (match) => `<mark>${match}</mark>`
          );
        }
      }
      return highlightedText;
    },
    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
  },
  computed: {
    sectionHeaderLabel() {
      return this.applicant && this.applicant.companyFrom === '自社'
        ? '自社HP経由登録'
        : '他ルート経由登録';
    },
    joinedDate() {
      return moment(this.applicant.joinedDate)
        .utcOffset('+0900')
        .format('YYYY-MM-DD');
    },
    updateDate() {
      if (this.applicant.updatedDate == null) return '';
      return moment(this.applicant.updatedDate)
        .utcOffset('+0900')
        .format('YYYY-MM-DD');
    },
    lastJob() {
      const { lastJob } = this.history;
      const { child } = lastJob;
      return `${lastJob.title} / ${child.title}`;
    },
    enrollment_period() {
      let enrolled = { from: {}, to: {} };
      
      // Check if enrollment period data exists
      if (!this.history.enrollment_period || !this.history.enrollment_period.from) {
        // No enrollment period data available - return special marker
        return null;
      }

      // Process enrollment period based on employment status
      let fromDate = new Date(this.history.enrollment_period.from);
      enrolled.from.year = String(fromDate.getUTCFullYear());
      enrolled.from.month = ('00' + (fromDate.getUTCMonth() + 1)).slice(-2);

      // Determine if this is completed employment (has end date) or ongoing
      const isCompletedEmployment = this.history.currently_enrolled === false || 
                                   (this.history.currently_enrolled == null && this.history.enrollment_period.to);

      if (isCompletedEmployment && this.history.enrollment_period.to) {
        // Completed employment - show both from and to dates
        let toDate = new Date(this.history.enrollment_period.to);
        enrolled.to.year = String(toDate.getUTCFullYear());
        enrolled.to.month = ('00' + (toDate.getUTCMonth() + 1)).slice(-2);
      } else {
        // Ongoing employment - no end date
        enrolled.to.year = '';
        enrolled.to.month = '';
      }

      return enrolled;
    },
    yearsAttended() {
      if (
        this.history.currently_enrolled === false &&
        typeof this.history.enrollment_period === typeof {}
      ) {
        let fromDate = new Date(this.history.enrollment_period.from);
        let toDate = new Date(this.history.enrollment_period.to);
        let diffMS = toDate.getTime() - fromDate.getTime();
        let yearInMS = 1000 * 3600 * 24 * 364;
        return Math.floor(diffMS / yearInMS);
      } else if (
        this.history.currently_enrolled === true &&
        this.history.enrollment_period &&
        this.history.enrollment_period.from
      ) {
        let fromDate = new Date(this.history.enrollment_period.from);
        let toDate = new Date();
        let diffMS = toDate.getTime() - fromDate.getTime();
        let yearInMS = 1000 * 3600 * 24 * 364;
        return Math.floor(diffMS / yearInMS);
      } else {
        return this.history.yearsAttended || 0;
      }
    },
    currently_enrolled() {
      if (
        this.history.currently_enrolled !== undefined &&
        this.history.currently_enrolled !== null
      ) {
        return this.history.currently_enrolled ? '在籍中' : '離職中';
      } else {
        //prev value
        return this.history.employmentSituation || false;
      }
    },
    events() {
      return this.eventHistory;
    },
  },
  mounted() {
    this.$emit('employmentLoaded');
  },
};
</script>

<style scoped>
:deep(mark) {
  background-color: yellow !important;
}
.word-break {
  word-break: break-word !important;
}
.whitespace-pre-wrap {
  white-space: pre-wrap !important;
}
.bar-highlight {
  display: inline-block;
  width: 4px; /* wider bar */
  background-color: #3c3c3c; /* solid black */
}

.resumetd {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.resumetd a {
  white-space: pre;
}
.hitstoryth,
.resumeth {
  width: 15%;
}
.event {
  border: none !important;
  font-size: 12px;
  white-space: normal !important;
  word-break: break-word;
  min-width: 80px;
  color: #617776;
  padding: 5px 7px !important;
}
#eventLabel {
  color: #036d6c;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}
.preview-resume {
  font-size: 16px;
  color: #007bff;
  cursor: pointer;
}
.preview-resume.disabled {
  pointer-events: none;
}
.preview-resume:hover {
  text-decoration: underline;
}

.company-name {
  font-size: 14px;
  font-weight: 500;
  color: #000;
}

.gap-40 {
  gap: 40px;
}

.section-header {
  font-weight: bold;
  color: #000000;
  margin-bottom: 8px;
  font-size: 14px;
}

.section-header-inline {
  font-size: 14px;
  line-height: 1.6;
}

.section-header-inline .section-header {
  margin-bottom: 0;
  margin-right: 16px;
}

.section-row {
  /* margin-bottom: 4px; */
  font-size: 14px;
  line-height: 1.6;
}

.section-label {
  color: #036d6c;
  font-weight: bold;
  margin-right: 4px;
}

.section-value {
  color: #3c3c3c;
}

.section-text {
  color: #3c3c3c;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.section-text.resume-text {
  white-space: pre-wrap;
}
</style>
